import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotificationContext } from "cerulean";
import { Row, ContentCard, Button, ResponsiveFlex } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { entities, modules, cents } from "byzantine";
import filters from "byzantine/src/filters";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useShowUserNeedsApprovalAlert } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { PAYMENT_ACTIVITY_ROUTE } from "../../PaymentActivity/routes";
import { ACH_PAYMENT_ROUTE } from "./ACHPaymentRoutes";
import styles from "./ReceiptScreen.module.scss";

const ReceiptScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const features = useUserFeatures();
  const { sendNotification } = useNotificationContext();
  const { uuid } = useParams();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { showAlert } = useShowUserNeedsApprovalAlert("ach");
  const { payment_activity: paymentActivityEnabled } = features;
  const { resetForm } = form;

  if (!selectedRecipient) return null;

  const {
    values: { amount, recurringRule },
  } = form;
  const { name } = selectedRecipient;

  const handleClickDone = () => {
    if (paymentActivityEnabled) {
      window.location.assign(`/${PAYMENT_ACTIVITY_ROUTE}/${uuid}`);
    } else {
      window.location.assign("/");
    }
  };

  const handleClickCTA = () => {
    resetForm();
    navigate(`/${ACH_PAYMENT_ROUTE}`);
  };

  useEffect(() => {
    if (showAlert) {
      sendNotification({
        type: "negative",
        text: l10n.getString(
          "ach-payment-wire-approval-org-requires-users",
          null,
          "Your submission can’t be approved until another user has approval permissions."
        ),
      });
    }
  }, [showAlert]);

  const displayFrequency =
    recurringRule?.frequency && recurringRule?.isVisible;

  return (
    <div className={styles.receiptContainer}>
      <ContentCard>
        <div className={styles.cardContainer}>
          <h2 className="fontColor--heading">
            {achDualApprovalRequired
              ? l10n.getString("ach-payment-receipt-dual-approval-title")
              : l10n.getString("ach-payment-receipt-title")}
            <i className="narmi-icon-check margin--left--xs" />
          </h2>
          <div className={styles.contentContainer}>
            <ResponsiveFlex gapSize="l">
              <Row>
                <Row.Item>
                  <p>
                    {isDebitPayment
                      ? l10n.getString("single-ach-payment-recipient-collect-from")
                      : l10n.getString("single-ach-payment-recipient-send-to")}
                  </p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{name}</p>
                </Row.Item>
              </Row>
              <Row>
                <Row.Item>
                  <p>{l10n.getString("label-amount")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{`${filters.currency(cents(amount))}`}</p>
                </Row.Item>
              </Row>
              {displayFrequency ? (
                <Row>
                  <Row.Item>
                    <p>{l10n.getString("label-frequency")}</p>
                  </Row.Item>
                  <Row.Item shrink>
                    <p>{recurringRule.frequency}</p>
                  </Row.Item>
                </Row>
              ) : null}
              <Row>
                <Row.Item>
                  <p>{l10n.getString("label-delivery-speed")}</p>
                </Row.Item>
                <Row.Item shrink>
                  <p>{l10n.getString("ach-payment-receipt-delivery")}</p>
                </Row.Item>
              </Row>
            </ResponsiveFlex>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              label={l10n.getString("label-done")}
              testId="ach-payment-receipt-done"
              onClick={handleClickDone}
            />
            <Button
              kind="tertiary"
              label={l10n.getString("ach-payment-receipt-cta")}
              testId="ach-payment-receipt-cta-button"
              onClick={handleClickCTA}
            />
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default ReceiptScreen;
